/**
 * The radio, in whichever view is showing it.
 *
 * Both the cockpit and the berth have one, and both do the same three things:
 * keep a log, keep a queue of interrupts, and fold in whatever arrives from the
 * room. That was written out twice, thirty-five lines apiece and identical but
 * for one word — so it is here once instead.
 *
 * ── Why the log is per view and the conversation is not ─────────────────────
 *
 * The *conversation* belongs to the app: moving between a berth and the cockpit
 * should not lose what people have said. The *log* does not, because it also
 * holds transmissions only this view can see — the tutorial talking you onto a
 * pad, a station answering a hail — and those have no business following you
 * into the other view.
 *
 * So the app owns the chat and hands it over, and each view folds it into its
 * own log as it arrives.
 */

import { useEffect, useRef, useState } from 'react';

import type { RadioChannel, RadioMessage } from './FlightRadio';
import type { Alert } from './RadioAlert';
import type { ChatLine } from '../net/peers';

/** How much of the conversation is kept. The panel only shows the end of it. */
const LOG_LIMIT = 60;

export interface RadioState {
  messages: RadioMessage[];
  setMessages: React.Dispatch<React.SetStateAction<RadioMessage[]>>;
  alerts: Alert[];
  setAlerts: React.Dispatch<React.SetStateAction<Alert[]>>;
}

/** Only the channels the panel knows how to colour. Anything else is local. */
function channelOf(name: string): RadioChannel {
  return name === 'station' || name === 'faction' ? name : 'local';
}

export function useRadio(chat: readonly ChatLine[] | undefined, uuid: string | undefined): RadioState {
  const [messages, setMessages] = useState<RadioMessage[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  /*
   * How much of the conversation this view has already folded in.
   *
   * A count rather than a set of ids: the app only ever appends, so everything
   * past the mark is new and nothing before it can change. A ref rather than
   * state, because advancing it must not itself cause a render.
   */
  const folded = useRef(0);

  useEffect(() => {
    if (!chat) return;

    const fresh = chat.slice(folded.current);
    if (fresh.length === 0) return;
    folded.current = chat.length;

    /*
     * Appended, not merged and sorted.
     *
     * The log also holds this view's own transmissions, which have no timestamp
     * from the room and no business being ordered against one. Appending keeps
     * everything in the order it actually arrived, which is the order the
     * conversation happened in.
     */
    const spoken = fresh.filter((line) => !line.alert);
    if (spoken.length > 0) {
      setMessages((log) => [
        ...log.slice(-LOG_LIMIT),
        ...spoken.map((line) => ({
          id: `${line.at}-${line.uuid}`,
          channel: channelOf(line.channel),
          from: line.from,
          callsign: line.callsign,
          text: line.text,
          // Yours reads differently from everyone else's, and the room is what
          // decides whose it is — the uuid came off a verified token.
          own: line.uuid === uuid,
        })),
      ]);
    }

    // An alert is not a line in a log. Somebody spent a beacon to interrupt you,
    // so it interrupts.
    for (const line of fresh.filter((entry) => entry.alert)) {
      setAlerts((queue) => [
        ...queue,
        { id: `alert-${line.at}-${line.uuid}`, from: `${line.from} — system alert`, text: line.text },
      ]);
    }
  }, [chat, uuid]);

  return { messages, setMessages, alerts, setAlerts };
}
